import React, { useState, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form } from "@/components/ui/form";
import Stepper from "components/Stepper/newstepper";
import PartnerSelection from './PartnerSelection';
import FormFieldRenderer from './FormFieldRenderer';
import RemitterDetailsSection from './RemitterDetailsSection';
import ExtraFieldsSection from './ExtraFieldsSection';
import PartnerMarginSection from './PartnerMarginSection';
import { Option } from 'components/Form/form';
import { selectOptionType } from 'components/SelectBox';
import { IFormInputMap } from '../hooks/useDealForm';
import { setServicePartnerId, setRemitterName, setRemitterDOB, setRemitterPAN, setpanBinary, setaadhaarBinary } from '../../../redux/features/CreateDeal';
import { useDealData } from '../hooks/useDealData';

interface DealFormStepsProps {
  currentStep: number;
  onStepChange: (step: number) => void;
  servicePartners: selectOptionType[];
  servicePartner: string | number;
  onServicePartnerChange: (partnerId: string | number) => void;
  dealType: string;
  onDealTypeChange: (type: string) => void;
  partnerDeal: boolean;
  partnerOption: { label: string; value: number; status?: string; city?: string }[];
  createdBy: string;
  onPartnerChange: (partnerId: string) => void;
  showAddButton: boolean;
  onAddPartner: () => void;
  onInputChange: (value: string) => void;
  purpose: Option[];
  countryOption: Option[];
  isCalcLoading: boolean;
  limitCustomerRate: number;
  limitCustomerRate2: number;
  onSubmit: (data: any) => void;
  disabled?: boolean;
  currency: string;
  activeField: number;
  setActiveField: (field: number | ((prev: number) => number)) => void;
  shouldDisplayField: (fieldName: string) => boolean;
  getFormConfig: (currency: string, purpose: Option[]) => any[][];
  refetch: Record<string, () => Promise<void>>;
  branches: selectOptionType[];
}

const DealFormSteps: React.FC<DealFormStepsProps> = ({
  currentStep,
  onStepChange,
  servicePartners,
  servicePartner,
  onServicePartnerChange,
  dealType,
  onDealTypeChange,
  partnerDeal,
  partnerOption,
  createdBy,
  onPartnerChange,
  showAddButton,
  onAddPartner,
  onInputChange,
  purpose,
  countryOption,
  isCalcLoading,
  limitCustomerRate,
  limitCustomerRate2,
  onSubmit,
  disabled = false,
  currency,
  activeField,
  setActiveField,
  shouldDisplayField,
  getFormConfig,
  branches,
  refetch
}) => {
  const form = useFormContext<IFormInputMap>();
  const dispatch = useDispatch();
  const [showOptionalFields, setShowOptionalFields] = useState<boolean>(false);
  const [disableSteps, setDisableSteps] = useState<boolean>(false);
  const [extraFields, setExtraFields] = useState<any[]>([]);
  const [files, setFiles] = useState<{ [key: string]: File }>({});
  const [fileBinaries, setFileBinaries] = useState<{ [key: string]: string }>({});
  const [extraFieldValues, setExtraFieldValues] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<any[]>([]);

  const { fetchFields, branchOption } = useDealData();

  const level = localStorage.getItem("level");
  const steps = ["Basic Details", "Other details"];
  
  const {
    service_partner_id,
    partner_status,
    partner_margin,
    partner_walkin_margin,
    remitterDOB,
    remitterPAN,
    remitter_name,
    purpose_id_copy
  } = useAppSelector((state) => state.CreateDeal);

  const watchAllFields = form.watch();

  useEffect(() => {
    fetchFields(purpose_id_copy, servicePartner?.toString()).then((fields) => {
      setExtraFields(fields);
    });
  }, [ service_partner_id]);

  // Update form config when dependencies change
  useEffect(() => {
    const config = getFormConfig(currency, purpose, branches);
    const currentFormData = config[currentStep - 1];
    
    // Update country options in the form config
    const updatedFormData = currentFormData?.map((field: any) => {
      if (field.name === 'country') {
        return { ...field, options: countryOption };
      }
      if (field.name === 'amount') {
        return { ...field, currency: currency };
      }
      return field;
    });
    
    setFormData(updatedFormData || []);
  }, [currentStep, currency, purpose, countryOption, getFormConfig]);

  const isFieldDisabled = (index: number) =>
    disableSteps ? index > 1 : index > activeField;

  const isFormComplete = formData?.every(
    (field: any) =>
      field.optional ||
      Boolean(watchAllFields[field.name as keyof IFormInputMap])
  );

  const remitterFields = () => {
    if (
      currentStep === 2 &&
      servicePartner === 17 &&
      (!remitterDOB || !remitterPAN || !remitter_name)
    )
      return true;
    return false;
  };

  const extraFieldsValidation = () => {
    if (currentStep === 2 && extraFields.length > 0) {
      for (const field of extraFields) {
        switch (field.type) {
          case "file":
            if (!files[field.name]) return true;
            break;
          case "text":
          case "number":
          case "date":
            if (!extraFieldValues[field.name]) return true;
            break;
          default:
            break;
        }
      }
    }
    return false;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.handleSubmit((data) => {
      if (currentStep < getFormConfig(currency, purpose).length) {
        onStepChange(currentStep + 1);
      } else {
        onSubmit(data);
      }
    })();
  };

  const ToggleOptionalFields = () => (
    <button
      type="button"
      onClick={() => {
        if (showOptionalFields) {
          form.setValue("rate", "");
        }
        setShowOptionalFields((prev) => !prev);
      }}
      className="text-blue-700 relative -top-10 underline text-left text-sm mt-4"
    >
      {showOptionalFields ? "Cancel" : "Set Customer Rate"}
    </button>
  );


  return (
    <div className="w-full">
      <div className="relative flex">
        <div className="w-full">
          <Stepper steps={steps} currentStep={currentStep - 1} />
        </div>
        {level === "1" && currentStep !== 2 && (
          <div className="flex absolute -top-4 right-0 flex-row items-center justify-center h-full">
            <Controller
              name="servicePartner"
              control={form.control}
              defaultValue=""
              render={({ field }) => (
                  <Select
                      onValueChange={(value) => {
                          field.onChange(value);
                          onServicePartnerChange(value);
                          if (value !== "17") {
                              dispatch(setRemitterName(""));
                              dispatch(setRemitterDOB(""));
                              dispatch(setRemitterPAN(""));
                          }
                          if (value !== "20") {
                              dispatch(setpanBinary(""));
                              dispatch(setaadhaarBinary(""));
                              setFiles({});
                          }
                          dispatch(setServicePartnerId(value));
                      }}
                      value={service_partner_id as string}
                  >
                      <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Service Partner" />
                      </SelectTrigger>
                      <SelectContent>
                          <SelectGroup>
                              <SelectLabel>Select Partner</SelectLabel>
                              {servicePartners.map((item) => (
                                  <SelectItem
                                      className="hover:bg-blue_gray-lite-bg cursor-pointer"
                                      key={item.value}
                                      value={item.value as string}
                                  >
                                      {item.label}
                                  </SelectItem>
                              ))}
                          </SelectGroup>
                      </SelectContent>
                  </Select>
              )}
            />
          </div>
        )}
      </div>

      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="pb-8 relative overflow-auto flex flex-col gap-10 pl-10 pt-1"
        >
          {currentStep === 1 && level === "1" && (
            <PartnerSelection
              dealType={dealType}
              setDealType={onDealTypeChange}
              partnerDeal={partnerDeal}
              partnerOption={partnerOption}
              createdBy={createdBy}
              onPartnerChange={onPartnerChange}
              showAddButton={showAddButton}
              onAddPartner={onAddPartner}
              onInputChange={onInputChange}
              disabled={disabled}
              refetch={refetch}
              branches={branchOption}
            />
          )}          

          {formData?.map((data: any, index: number) => (
            <FormFieldRenderer
              key={data.name}
              data={data}
              index={index}
              isDisabled={false}
              showOptionalFields={showOptionalFields}
              isCalcLoading={isCalcLoading}
              limitCustomerRate={limitCustomerRate}
              limitCustomerRate2={limitCustomerRate2}
              servicePartnerId={service_partner_id}
              partnerStatus={partner_status}
              setActiveField={setActiveField}
              shouldDisplayField={shouldDisplayField}
              formDataLength={formData.length}
            />
          ))}

          {formData?.some((data: any) => data.optional && !data.alwaysShow) && (
            <ToggleOptionalFields />
          )}

          {currentStep === 1 && partner_status === "pending" && (
            <PartnerMarginSection isCalcLoading={isCalcLoading} />
          )}

          {currentStep === 2 && servicePartner === 17 && (
            <RemitterDetailsSection servicePartner={servicePartner} />
          )}

          {currentStep === 2 && extraFields?.length > 0 && (
            <ExtraFieldsSection
              extraFields={extraFields}
              files={files}
              setFiles={setFiles}
              fileBinaries={fileBinaries}
              setFileBinaries={setFileBinaries}
              extraFieldValues={extraFieldValues}
              setExtraFieldValues={setExtraFieldValues}
            />
          )}

          <div className="flex gap-3">
            {currentStep === 2 && (
              <button
                type="button"
                onClick={() => onStepChange(currentStep - 1)}
                className="px-4 py-[11px] w-fit rounded-lg border-blue-700 border"
              >
                Previous
              </button>
            )}
            <button
              type="submit"
              disabled={
                !isFormComplete ||
                disableSteps ||
                remitterFields() ||
                extraFieldsValidation() ||
                (currentStep === 1 && partner_status === "pending"
                  ? !partner_margin || !partner_walkin_margin
                  : false)
              }
              className={`px-4 py-[11px] w-fit rounded-lg bg-blue-700 text-white-A700 disabled:opacity-40 ${
                isFormComplete && !disableSteps ? "" : "opacity-40"
              }`}
            >
              {currentStep === getFormConfig(currency, purpose).length ? "Submit" : "Next"}
            </button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default DealFormSteps;